<template>
    <div v-if="risk" class="w-full flex flex-column relative surface-100 border-round-xl xl:border-round-2xl overflow-hidden">
        <div @click="toggleExpanded" class="flex flex-column xl:flex-row xl:align-items-center justify-content-between gap-2 p-3 cursor-pointer">
            <div class="flex flex-row flex-wrap align-items-center gap-2">
                <span class="flex text-sm xl:text-base text-900 font-medium">Риск-профиль</span>
                <Tag :severity="riskLevelSeverity(risk.level)" class="text-xs" :value="riskLevelLabel(risk.level)" />
                <Tag :severity="actionSeverity(risk.action)" class="text-xs" :value="actionLabel(risk.action)" />
                <Tag :severity="risk.hard_rule ? 'danger' : 'success'" class="text-xs" :value="risk.hard_rule ? 'Hard rule' : 'Без hard rule'" />
            </div>
            <div class="flex flex-row flex-wrap align-items-center gap-3 text-xs xl:text-sm text-600">
                <span class="text-900 font-semibold">Score: {{ risk.score }}</span>
                <span v-if="risk.evaluated_at">{{ formatDate(risk.evaluated_at) }}</span>
                <span v-if="totalFactors">Факторы: {{ totalFactors }}</span>
                <span :class="expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-500"></span>
            </div>
        </div>

        <div v-if="expanded" class="flex flex-column gap-3 p-3 pt-0">
            <div class="flex flex-row flex-wrap gap-2 text-sm">
                <div class="flex flex-column surface-0 border-round-lg p-2 min-w-8rem">
                    <span class="text-xs text-600 mb-1">Риск</span>
                    <span class="text-900 font-medium">{{ riskLevelLabel(risk.level) }}</span>
                </div>
                <div class="flex flex-column surface-0 border-round-lg p-2 min-w-8rem">
                    <span class="text-xs text-600 mb-1">Действие</span>
                    <span class="text-900 font-medium">{{ actionLabel(risk.action) }}</span>
                </div>
                <div class="flex flex-column surface-0 border-round-lg p-2 min-w-8rem">
                    <span class="text-xs text-600 mb-1">Hard rule</span>
                    <span class="text-900 font-medium">{{ formatValue(risk.hard_rule) }}</span>
                </div>
            </div>

            <div v-if="risk.factors && risk.factors.length" class="flex flex-column gap-2">
            <span class="text-sm text-600">Факторы риска:</span>
            <div v-for="factor in risk.factors" :key="factor.code" class="flex flex-column xl:flex-row xl:align-items-center gap-2 surface-0 border-round-lg p-2 text-sm">
                <Tag :severity="factorSeverity(factor.type)" class="text-xs" :value="factorTypeLabel(factor.type)" />
                <span class="font-medium text-900">{{ factor.description_ru || factor.code }}</span>
                <span class="text-600">score: {{ factor.score }}</span>
                <span class="text-600">value: {{ formatValue(factor.value) }}</span>
            </div>
            </div>

            <div v-if="risk.trust_factors && risk.trust_factors.length" class="flex flex-column gap-2">
            <span class="text-sm text-600">Факторы доверия:</span>
            <div v-for="factor in risk.trust_factors" :key="factor.code" class="flex flex-column xl:flex-row xl:align-items-center gap-2 surface-0 border-round-lg p-2 text-sm">
                <Tag :severity="factorSeverity(factor.type)" class="text-xs" :value="factorTypeLabel(factor.type)" />
                <span class="font-medium text-900">{{ factor.description_ru || factor.code }}</span>
                <span class="text-600">score: {{ factor.score }}</span>
                <span class="text-600">value: {{ formatValue(factor.value) }}</span>
            </div>
            </div>

            <div v-if="risk.interaction_factors && risk.interaction_factors.length" class="flex flex-column gap-2">
            <span class="text-sm text-600">Интеракции:</span>
            <div v-for="factor in risk.interaction_factors" :key="factor.code" class="flex flex-column xl:flex-row xl:align-items-center gap-2 surface-0 border-round-lg p-2 text-sm">
                <Tag :severity="factorSeverity(factor.type)" class="text-xs" :value="factorTypeLabel(factor.type)" />
                <span class="font-medium text-900">{{ factor.description_ru || factor.code }}</span>
                <span class="text-600">score: {{ factor.score }}</span>
                <span class="text-600">value: {{ formatValue(factor.value) }}</span>
            </div>
            </div>

            <div v-if="contextItems.length" class="flex flex-column gap-2">
            <span class="text-sm text-600">Контекст:</span>
            <div class="grid m-0">
                <div v-for="item in contextItems" :key="item.key" class="col-12 md:col-6 xl:col-4 p-1">
                    <div class="flex flex-column surface-0 border-round-lg p-2 h-full text-sm">
                        <span class="text-600 mb-1">{{ item.label }}</span>
                        <span class="text-900 font-medium">{{ item.value }}</span>
                    </div>
                </div>
            </div>
            </div>

            <div v-if="risk.admin_notification" class="flex flex-column gap-2 text-sm">
            <span class="text-sm text-600">Уведомление админа:</span>
            <div class="flex flex-row flex-wrap gap-2 surface-0 border-round-lg p-2">
                <Tag :severity="risk.admin_notification.sent ? 'success' : 'warn'" class="text-xs" :value="risk.admin_notification.sent ? 'Отправлено' : 'Не отправлено'" />
                <span v-if="risk.admin_notification.sent_at" class="text-600">{{ formatDate(risk.admin_notification.sent_at) }}</span>
                <span v-if="risk.admin_notification.last_error" class="text-red-500">{{ risk.admin_notification.last_error }}</span>
            </div>
            </div>
        </div>
    </div>
</template>

<script>
import Helper from '@/assets/js/Helper.js'

export default {
    name: 'RiskProfile',
    props: {
        risk: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            expanded: false
        }
    },
    computed: {
        totalFactors() {
            if(!this.risk) return 0
            return this.getCount(this.risk.factors) + this.getCount(this.risk.trust_factors) + this.getCount(this.risk.interaction_factors)
        },
        contextItems() {
            if(!this.risk || !this.risk.context_summary) return []
            return Object.keys(this.risk.context_summary).map(key => ({
                key,
                label: this.contextLabel(key),
                value: this.formatValue(this.risk.context_summary[key])
            }))
        }
    },
    methods: {
        toggleExpanded(){
            this.expanded = !this.expanded
        },
        getCount(items){
            return (items && items.length) ? items.length : 0
        },
        formatDate(value){
            return this.helper.format_date_short(value)
        },
        formatValue(value){
            if(value === true) return 'Да'
            if(value === false) return 'Нет'
            if(value === null || value === undefined || value === '') return '-'
            return value
        },
        riskLevelLabel(level){
            const labels = {
                low: 'Низкий риск',
                medium: 'Средний риск',
                high: 'Высокий риск',
                critical: 'Критический риск'
            }
            return labels[level] || level || '-'
        },
        riskLevelSeverity(level){
            const severity = {
                low: 'success',
                medium: 'warn',
                high: 'danger',
                critical: 'danger'
            }
            return severity[level] || 'secondary'
        },
        actionLabel(action){
            const labels = {
                allow: 'Разрешить',
                review: 'На проверку',
                block: 'Блокировать'
            }
            return labels[action] || action || '-'
        },
        actionSeverity(action){
            const severity = {
                allow: 'success',
                review: 'warn',
                block: 'danger'
            }
            return severity[action] || 'secondary'
        },
        factorTypeLabel(type){
            const labels = {
                risk: 'Риск',
                trust: 'Доверие',
                interaction: 'Интеракция'
            }
            return labels[type] || type || '-'
        },
        factorSeverity(type){
            const severity = {
                risk: 'danger',
                trust: 'success',
                interaction: 'info'
            }
            return severity[type] || 'secondary'
        },
        contextLabel(key){
            const labels = {
                amount_base: 'Сумма в базе',
                asset_code: 'Актив',
                account_age_days: 'Возраст аккаунта, дней',
                transfer_count_24h: 'Переводов за 24 часа',
                completed_transfer_count: 'Успешных переводов',
                total_transfer_count: 'Всего переводов',
                average_completed_amount_base: 'Средняя успешная сумма',
                recipient_unique_user_count: 'Уникальных пользователей получателя',
                known_recipient: 'Известный получатель',
                verified: 'Верифицирован'
            }
            return labels[key] || key
        }
    },
    beforeCreate() {
        this.helper = new Helper()
    }
}
</script>